import { reportEvent } from '~/domains/analytics';

import type { MarketSubCategory } from './services/interfaces/market.types';
import type { MarketType, TeamType } from './components/MarketCard/types';

/**
 * Named for the widget rather than the surface, with `location` naming the surface, so the same
 * events cover the widget wherever it is embedded next.
 */
export type PredictionsWidgetLocation = 'Home';
export type PredictionsWidgetPlacement = 'featured' | 'ticker';
type PredictionsWidgetGameState = 'pregame' | 'live';

type WidgetEvent = { location: PredictionsWidgetLocation };

type MarketTapped = WidgetEvent & {
  gameId: string | undefined;
  league: MarketSubCategory | undefined;
  placement: PredictionsWidgetPlacement;
  selection: `${MarketType}_${TeamType}`;
  odds: string | undefined;
  gameState: PredictionsWidgetGameState;
};

type GameDetailsTapped = Pick<MarketTapped, 'location' | 'gameId' | 'league' | 'placement'>;

export const reportPredictionsWidgetViewed = ({
  location,
  featuredGameId,
  featuredLeague,
  tickerGameCount,
}: WidgetEvent & {
  featuredGameId: string | undefined;
  featuredLeague: MarketSubCategory | undefined;
  tickerGameCount: number;
}) => {
  reportEvent('Predictions Widget > Viewed', {
    location,
    featured_game_id: featuredGameId,
    featured_league: featuredLeague,
    ticker_game_count: tickerGameCount,
  });
};

export const reportPredictionsWidgetMarketTapped = ({
  location,
  gameId,
  league,
  placement,
  selection,
  odds,
  gameState,
}: MarketTapped) => {
  reportEvent('Predictions Widget > Market Tapped', {
    location,
    game_id: gameId,
    league,
    placement,
    selection,
    odds,
    game_state: gameState,
  });
};

export const reportPredictionsWidgetGameDetailsTapped = ({
  location,
  gameId,
  league,
  placement,
}: GameDetailsTapped) => {
  reportEvent('Predictions Widget > Game Details Tapped', {
    location,
    game_id: gameId,
    league,
    placement,
  });
};

export const reportPredictionsWidgetAllMarketsTapped = ({ location }: WidgetEvent) => {
  reportEvent('Predictions Widget > All Markets Tapped', { location });
};

export const reportPredictionsWidgetTickerScrolled = ({
  location,
  maxIndexReached,
}: WidgetEvent & { maxIndexReached: number }) => {
  reportEvent('Predictions Widget > Ticker Scrolled', {
    location,
    max_index_reached: maxIndexReached,
  });
};
